import React, { useState, useEffect, useRef } from 'react';
import { editWheel, deleteWheel, uploadWheelImages, deleteWheelImage, setPrimaryWheelImage } from '../api';
import './wheelprofile.css';

const WheelProfile = ({ wheel, onClose, onWheelUpdated, onWheelDeleted }) => {
  const [editedWheel, setEditedWheel] = useState({ ...wheel });
  const [images, setImages] = useState(wheel.images || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);

  const imageInputRef = useRef(null);

  useEffect(() => {
    setEditedWheel({ ...wheel });
    setImages(wheel.images || []);
    setHasUnsavedChanges(false);
  }, [wheel]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedWheel((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onClose(null));
    } else {
      onClose({ ...editedWheel, images });
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (pendingAction) pendingAction();
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
    setPendingAction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: editedWheel.name || '',
        paint_color: editedWheel.paint_color || '',
        paint_code: editedWheel.paint_code || '',
        part_number: editedWheel.part_number || '',
        notes: editedWheel.notes || '',
      };
      const updated = await editWheel(wheel.id, payload);
      setEditedWheel(updated);
      setHasUnsavedChanges(false);
      if (onWheelUpdated) onWheelUpdated({ ...updated, images });
      onClose({ ...updated, images });
    } catch (error) {
      console.error('Error saving wheel:', error);
      alert('Failed to save wheel.');
    }
  };

  const handleDeleteWheel = async () => {
    try {
      await deleteWheel(wheel.id);
      if (onWheelDeleted) onWheelDeleted(wheel.id);
      onClose(null);
    } catch (error) {
      console.error('Error deleting wheel:', error);
      alert('Failed to delete wheel.');
    }
  };

  // --- Image handling ---

  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          let resolved = false;
          img.onload = () => {
            if (resolved) return;
            try {
              const canvas = document.createElement('canvas');
              let width = img.width;
              let height = img.height;
              if (width > maxWidth) {
                height = (height / width) * maxWidth;
                width = maxWidth;
              }
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);
              canvas.toBlob((blob) => {
                if (resolved) return;
                img.onerror = null;
                img.onload = null;
                img.src = '';
                canvas.width = 0;
                canvas.height = 0;
                if (!blob) { resolved = true; resolve(file); return; }
                const compressed = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
                resolved = true;
                resolve(compressed);
              }, 'image/jpeg', quality);
            } catch (err) {
              if (!resolved) { resolved = true; resolve(file); }
            }
          };
          img.onerror = () => { if (!resolved) { resolved = true; resolve(file); } };
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      } catch (err) {
        resolve(file);
      }
    });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploadingImages(true);
    try {
      const toUpload = [];
      for (const file of Array.from(files)) {
        const sizeMB = file.size / 1024 / 1024;
        const compressed = sizeMB < 0.5 ? file : await compressImage(file);
        toUpload.push(compressed);
      }
      const newImages = await uploadWheelImages(wheel.id, toUpload);
      const allImages = [...images, ...newImages];
      setImages(allImages);
      if (onWheelUpdated) onWheelUpdated({ ...editedWheel, images: allImages });
      if (imageInputRef.current) imageInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images.');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!window.confirm('Delete this image?')) return;
    try {
      await deleteWheelImage(imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image.');
    }
  };

  const handleSetPrimary = async (imageId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    try {
      await setPrimaryWheelImage(imageId);
      setImages((prev) =>
        prev.map((img) => ({ ...img, is_primary: img.id === imageId }))
      );
    } catch (error) {
      console.error('Error setting primary image:', error);
      alert('Failed to set primary image.');
    }
  };

  // Gallery
  const getAllImages = () => images;

  const getCurrentGalleryImage = () => {
    const all = getAllImages();
    return all[currentGalleryIndex] || null;
  };

  const handleOpenGallery = (index) => {
    setCurrentGalleryIndex(index);
    setShowFullscreenImage(true);
    setShowFullscreenControls(true);
  };

  const handleGalleryNext = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const total = getAllImages().length;
    setCurrentGalleryIndex((prev) => (prev + 1) % total);
  };

  const handleGalleryPrev = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const total = getAllImages().length;
    setCurrentGalleryIndex((prev) => (prev - 1 + total) % total);
  };

  const handleCloseFullscreen = (e, forceClose = false) => {
    if (forceClose || (e && e.target === e.currentTarget)) {
      setShowFullscreenImage(false);
      setShowFullscreenControls(true);
    }
  };

  const primaryImage = images.find((img) => img.is_primary) || images[0];

  return (
    <div className="wheelprofile">
      {/* Header bar */}
      <div className="wheelprofile-header">
        <div className="wheelprofile-header-left">
          <span className="wheelprofile-title">
            {editedWheel.name || 'Wheel Profile'}
          </span>
          {wheel.id && <span className="wheelprofile-id">#{wheel.id}</span>}
        </div>
        <div className="wheelprofile-header-right">
          <button className="wheelprofile-save-btn" onClick={handleSubmit} disabled={!hasUnsavedChanges}>
            Save
          </button>
          <button className="wheelprofile-close-btn" onClick={handleClose}>✕</button>
        </div>
      </div>

      <div className="wheelprofile-body">
        {/* Left: primary image + gallery strip */}
        <div className="wheelprofile-left">
          <div
            className="wheelprofile-main-photo"
            onClick={() => primaryImage && handleOpenGallery(images.indexOf(primaryImage))}
          >
            {primaryImage ? (
              <img
                src={primaryImage.image}
                alt="Wheel"
                className="wheelprofile-main-img"
              />
            ) : (
              <div className="wheelprofile-no-photo">
                <span>No Photo</span>
              </div>
            )}
            {primaryImage && (
              <div className="wheelprofile-photo-overlay">
                <span>View</span>
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          {images.length > 0 && (
            <div className="wheelprofile-thumbnails">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  className={`wheelprofile-thumb ${img.is_primary ? 'primary' : ''}`}
                  onClick={() => handleOpenGallery(idx)}
                >
                  <img src={img.image} alt={`Wheel img ${idx + 1}`} className="wheelprofile-thumb-img" />
                  <div className="wheelprofile-thumb-actions">
                    {!img.is_primary && (
                      <button
                        className="wheelprofile-thumb-btn primary-btn"
                        title="Set as primary"
                        onClick={(e) => handleSetPrimary(img.id, e)}
                      >★</button>
                    )}
                    <button
                      className="wheelprofile-thumb-btn delete-btn"
                      title="Delete"
                      onClick={(e) => handleDeleteImage(img.id, e)}
                    >×</button>
                  </div>
                  {img.is_primary && <div className="wheelprofile-primary-badge">Primary</div>}
                </div>
              ))}
              <div
                className="wheelprofile-thumb wheelprofile-add-thumb"
                onClick={() => imageInputRef.current?.click()}
              >
                <span>{isUploadingImages ? '...' : '+'}</span>
              </div>
            </div>
          )}

          {images.length === 0 && (
            <button
              className="wheelprofile-upload-btn"
              onClick={() => imageInputRef.current?.click()}
              disabled={isUploadingImages}
            >
              {isUploadingImages ? 'Uploading...' : 'Add Photo'}
            </button>
          )}

          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
        </div>

        {/* Right: fields */}
        <div className="wheelprofile-right">
          <form className="wheelprofile-form" onSubmit={handleSubmit}>
            <div className="wheelprofile-field">
              <label className="wheelprofile-label">Name</label>
              <input
                className="wheelprofile-input"
                name="name"
                value={editedWheel.name || ''}
                onChange={handleChange}
                placeholder=""
              />
            </div>

            <div className="wheelprofile-field">
              <label className="wheelprofile-label">Paint Color</label>
              <input
                className="wheelprofile-input"
                name="paint_color"
                value={editedWheel.paint_color || ''}
                onChange={handleChange}
                placeholder=""
              />
            </div>

            <div className="wheelprofile-field">
              <label className="wheelprofile-label">Paint Code</label>
              <input
                className="wheelprofile-input"
                name="paint_code"
                value={editedWheel.paint_code || ''}
                onChange={handleChange}
                placeholder=""
              />
            </div>

            <div className="wheelprofile-field">
              <label className="wheelprofile-label">Part #</label>
              <input
                className="wheelprofile-input"
                name="part_number"
                value={editedWheel.part_number || ''}
                onChange={handleChange}
                placeholder=""
              />
            </div>

            <div className="wheelprofile-field">
              <label className="wheelprofile-label">Notes</label>
              <textarea
                className="wheelprofile-textarea"
                name="notes"
                value={editedWheel.notes || ''}
                onChange={handleChange}
                placeholder=""
                rows={4}
              />
            </div>

            <div className="wheelprofile-actions">
              {/* Delete button hidden for v1 */}
              {false && (
                <button
                  type="button"
                  className="wheelprofile-delete-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Wheel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Unsaved changes warning */}
      {showUnsavedWarning && (
        <div className="wheelprofile-dialog-overlay">
          <div className="wheelprofile-dialog">
            <p>You have unsaved changes. Discard them?</p>
            <div className="wheelprofile-dialog-actions">
              <button className="wheelprofile-dialog-confirm" onClick={handleConfirmClose}>Discard</button>
              <button className="wheelprofile-dialog-cancel" onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="wheelprofile-dialog-overlay">
          <div className="wheelprofile-dialog">
            <p>Delete this wheel record permanently?</p>
            <div className="wheelprofile-dialog-actions">
              <button className="wheelprofile-dialog-confirm" onClick={handleDeleteWheel}>Delete</button>
              <button className="wheelprofile-dialog-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen image gallery */}
      {showFullscreenImage && (
        <div
          className="wheelprofile-fullscreen-overlay"
          onClick={(e) => handleCloseFullscreen(e)}
        >
          <div className="wheelprofile-fullscreen-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="wheelprofile-fs-close"
              onClick={(e) => handleCloseFullscreen(e, true)}
            >✕</button>

            {getAllImages().length > 1 && (
              <>
                <button className="wheelprofile-fs-prev" onClick={handleGalleryPrev}>‹</button>
                <button className="wheelprofile-fs-next" onClick={handleGalleryNext}>›</button>
              </>
            )}

            {getCurrentGalleryImage() && (
              <img
                src={getCurrentGalleryImage().image}
                alt="Wheel full"
                className="wheelprofile-fs-img"
              />
            )}

            {getAllImages().length > 1 && (
              <div className="wheelprofile-fs-counter">
                {currentGalleryIndex + 1} / {getAllImages().length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WheelProfile;
